export interface TraceContext {
  traceId: string;
  spanId: string;
  traceparent: string;
}

const VERSION = '00';
const DEFAULT_FLAGS = '01';
const TRACE_ID_PATTERN = /^[0-9a-f]{32}$/;

export function createTraceId(): string {
  return crypto.randomUUID().replaceAll('-', '');
}

function createSpanId(): string {
  return crypto.randomUUID().replaceAll('-', '').slice(0, 16);
}

function normalizeTraceId(traceId: string): string | null {
  const normalized = traceId.toLowerCase();
  if (!TRACE_ID_PATTERN.test(normalized)) return null;
  if ([...normalized].every((char) => char === '0')) return null;
  return normalized;
}

export function createTraceContext(traceId = createTraceId()): TraceContext {
  const normalizedTraceId = normalizeTraceId(traceId) ?? createTraceId();
  const spanId = createSpanId();
  return {
    traceId: normalizedTraceId,
    spanId,
    traceparent: `${VERSION}-${normalizedTraceId}-${spanId}-${DEFAULT_FLAGS}`,
  };
}

interface FlowState {
  traceId: string;
  path: string;
}

let flowState: FlowState | null = null;

/**
 * 브라우저 페이지뷰(경로) 단위로 traceId를 묶는다.
 * 같은 경로에서 재호출되면 기존 flow traceId를 재사용하되, spanId는
 * 호출마다 새로 발급한다 — "페이지뷰 1건 = trace 1개, 요청마다 span 1개".
 * 서버 사이드(SSR/route handler)는 페이지뷰 개념이 없어 요청 단위(기존
 * createTraceContext)로 위임한다.
 */
export function getFlowTraceContext(): TraceContext {
  if (typeof window === 'undefined') return createTraceContext();

  const currentPath = window.location.pathname;
  if (!flowState || flowState.path !== currentPath) {
    flowState = { traceId: createTraceId(), path: currentPath };
  }

  const spanId = createSpanId();
  return {
    traceId: flowState.traceId,
    spanId,
    traceparent: `${VERSION}-${flowState.traceId}-${spanId}-${DEFAULT_FLAGS}`,
  };
}

/**
 * 테스트 전용: 모듈 스코프 flow 상태를 초기화한다.
 * 프로덕션 코드에서는 호출하지 않는다.
 */
export function resetFlowTraceContextForTest(): void {
  flowState = null;
}

const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-([0-9a-f]{16})-[0-9a-f]{2}$/;

/**
 * Loki namespace(`{profile}-wkwk-web`)와 동일한 값을 도출한다.
 * FE 배포 컨테이너에는 `PROFILE` env가 주입되지 않으므로(GHA 워크플로우
 * env로만 존재, 이미지 태그/k8s manifest 조작용), 빌드 시 `.env.ssm.*`에서
 * 주입되는 `NEXT_PUBLIC_MODE`(development/production)를 dev/prod로
 * 정규화해 사용한다. 둘 다 없으면 로컬 개발 환경으로 간주해 'local'.
 */
export function resolveProfile(): string {
  const profile = process.env.PROFILE;
  if (profile) return profile;

  const mode = process.env.NEXT_PUBLIC_MODE;
  if (mode === 'production') return 'prod';
  if (mode === 'development') return 'dev';

  return 'local';
}

/**
 * FE 서버(SSR/route handler)발 API 호출을 stdout에 1줄 남기기 위한 포맷터.
 * be/ai 로그와 같은 `[traceId|s:spanId|p:-]` 골격을 유지해 Loki에서
 * traceId 문자열 매칭으로 FE hop까지 엮을 수 있게 한다.
 * 맨 앞에는 Loki namespace와 통일된 `[{profile}-wkwk-web]` 서비스 식별자를 붙인다.
 */
export function formatTraceLog(
  traceparent: string,
  method: string,
  url: string,
): string {
  const match = TRACEPARENT_PATTERN.exec(traceparent);
  const traceId = match?.[1] ?? '-';
  const spanId = match?.[2] ?? '-';
  return `[${resolveProfile()}-wkwk-web] [trace] [${traceId}|s:${spanId}|p:-] -> ${method.toUpperCase()} ${url}`;
}
